type ShiftAuditCommentPromptOptions = {
  title: string
  description: string
  placeholder: string
  missingCommentError: string
}

type ShiftAuditCommentPromptRequest = ShiftAuditCommentPromptOptions & {
  id: number
  resolve: (comment: string) => void
  reject: (error: Error) => void
}

type ShiftAuditCommentPromptListener = (request: ShiftAuditCommentPromptRequest | null) => void

const listeners = new Set<ShiftAuditCommentPromptListener>()
const requestQueue: ShiftAuditCommentPromptRequest[] = []

let activeRequest: ShiftAuditCommentPromptRequest | null = null
let nextRequestId = 1
let hostCount = 0
const SHIFT_AUDIT_COMMENT_PROMPT_UNMOUNTED_ERROR =
  "Shift audit comment dialog was unmounted before the prompt completed."

const emitPromptState = () => {
  listeners.forEach((listener) => listener(activeRequest))
}

const activateNextPrompt = () => {
  if (activeRequest || requestQueue.length === 0) return

  activeRequest = requestQueue[0]
  emitPromptState()
}

const rejectAllPendingShiftAuditCommentPrompts = (message: string) => {
  if (!activeRequest && requestQueue.length === 0) {
    return
  }

  const pendingRequests = [...requestQueue]
  requestQueue.length = 0
  activeRequest = null
  emitPromptState()

  pendingRequests.forEach((request) => {
    request.reject(new Error(message))
  })
}

export const finishActiveShiftAuditCommentPrompt = () => {
  if (!activeRequest) return

  requestQueue.shift()
  activeRequest = null
  emitPromptState()
  activateNextPrompt()
}

export const getActiveShiftAuditCommentPrompt = () => activeRequest

export const registerShiftAuditCommentPromptHost = () => {
  hostCount += 1

  return () => {
    hostCount = Math.max(0, hostCount - 1)

    if (hostCount === 0) {
      rejectAllPendingShiftAuditCommentPrompts(SHIFT_AUDIT_COMMENT_PROMPT_UNMOUNTED_ERROR)
    }
  }
}

export const subscribeToShiftAuditCommentPrompt = (listener: ShiftAuditCommentPromptListener) => {
  listeners.add(listener)
  listener(activeRequest)

  return () => {
    listeners.delete(listener)
  }
}

export const requestShiftAuditCommentPrompt = (
  options: ShiftAuditCommentPromptOptions,
): Promise<string> =>
  new Promise((resolve, reject) => {
    if (hostCount === 0) {
      reject(new Error("Shift audit comment dialog is not mounted for this page."))
      return
    }

    requestQueue.push({
      id: nextRequestId++,
      ...options,
      resolve,
      reject,
    })
    activateNextPrompt()
  })
